import React from 'react';
import styled, { keyframes } from 'styled-components';

// Skeleton animation
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Base skeleton component ($-prefixed props are not forwarded to the DOM)
const SkeletonBase = styled.div<{ $width?: string; $height?: string; $borderRadius?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
  border-radius: ${props => props.$borderRadius || '4px'};
  display: block;
`;

// Card skeleton
export const SkeletonCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
`;

// Text skeleton variants
export const SkeletonText: React.FC<{ lines?: number; width?: string }> = ({ 
  lines = 1, 
  width = '100%' 
}) => (
  <>
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonBase
        key={index}
        $height="16px"
        $width={index === lines - 1 ? '60%' : width}
        style={{ marginBottom: index < lines - 1 ? '8px' : '0' }}
      />
    ))}
  </>
);

// Title skeleton
export const SkeletonTitle: React.FC<{ width?: string }> = ({ width = '200px' }) => (
  <SkeletonBase $height="24px" $width={width} $borderRadius="6px" />
);

// Avatar skeleton
export const SkeletonAvatar: React.FC<{ size?: string }> = ({ size = '40px' }) => (
  <SkeletonBase
    $width={size}
    $height={size}
    $borderRadius="50%"
  />
);

// Button skeleton
export const SkeletonButton: React.FC<{ width?: string; height?: string }> = ({ 
  width = '120px', 
  height = '40px' 
}) => (
  <SkeletonBase
    $width={width}
    $height={height}
    $borderRadius="8px"
  />
);

// Table skeleton
export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => (
  <div>
    {/* Table header */}
    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
      {Array.from({ length: columns }).map((_, index) => (
        <SkeletonBase
          key={`header-${index}`}
          $height="20px"
          $width="120px"
        />
      ))}
    </div>
    {/* Table rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <SkeletonBase
            key={`cell-${rowIndex}-${colIndex}`}
            $height="16px"
            $width={colIndex === 0 ? '150px' : '100px'}
          />
        ))}
      </div>
    ))}
  </div>
);

// Grid skeleton for cards
export const SkeletonGrid: React.FC<{ 
  items?: number; 
  columns?: number;
  cardHeight?: string;
}> = ({ 
  items = 6, 
  columns = 3,
  cardHeight = '200px'
}) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: `repeat(${columns}, 1fr)`, 
    gap: '20px' 
  }}>
    {Array.from({ length: items }).map((_, index) => (
      <SkeletonCard key={index} style={{ height: cardHeight }}>
        <SkeletonBase $height="20px" $width="80%" style={{ marginBottom: '12px' }} />
        <SkeletonBase $height="16px" $width="100%" style={{ marginBottom: '8px' }} />
        <SkeletonBase $height="16px" $width="60%" style={{ marginBottom: '16px' }} />
        <SkeletonBase $height="32px" $width="100%" $borderRadius="8px" />
      </SkeletonCard>
    ))}
  </div>
);

// Chart skeleton
export const SkeletonChart: React.FC<{ height?: string }> = ({ height = '300px' }) => (
  <SkeletonCard>
    <SkeletonBase $height="20px" $width="200px" style={{ marginBottom: '20px' }} />
    <SkeletonBase $height={height} $width="100%" $borderRadius="8px" />
  </SkeletonCard>
);

// List skeleton
export const SkeletonList: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <div>
    {Array.from({ length: items }).map((_, index) => (
      <div key={index} style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px', 
        padding: '12px 0',
        borderBottom: index < items - 1 ? '1px solid #e5e7eb' : 'none'
      }}>
        <SkeletonAvatar size="32px" />
        <div style={{ flex: 1 }}>
          <SkeletonBase $height="16px" $width="70%" style={{ marginBottom: '4px' }} />
          <SkeletonBase $height="14px" $width="40%" />
        </div>
        <SkeletonBase $height="16px" $width="60px" />
      </div>
    ))}
  </div>
);

// Stats card skeleton
export const SkeletonStatsCard: React.FC = () => (
  <SkeletonCard>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <SkeletonBase $width="48px" $height="48px" $borderRadius="12px" />
    </div>
    <SkeletonBase $height="32px" $width="80px" style={{ marginBottom: '4px' }} />
    <SkeletonBase $height="14px" $width="120px" style={{ marginBottom: '8px' }} />
    <SkeletonBase $height="12px" $width="100px" />
  </SkeletonCard>
);

// Form skeleton
export const SkeletonForm: React.FC<{ fields?: number }> = ({ fields = 4 }) => (
  <SkeletonCard>
    {Array.from({ length: fields }).map((_, index) => (
      <div key={index} style={{ marginBottom: '20px' }}>
        <SkeletonBase $height="14px" $width="100px" style={{ marginBottom: '8px' }} />
        <SkeletonBase $height="40px" $width="100%" $borderRadius="8px" />
      </div>
    ))}
    <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
      <SkeletonButton width="100px" />
      <SkeletonButton width="80px" />
    </div>
  </SkeletonCard>
);

// Dashboard specific skeletons
export const SkeletonDashboard: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
    {/* Welcome section skeleton */}
    <SkeletonCard style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
        <SkeletonBase $width="60px" $height="60px" $borderRadius="12px" />
        <div style={{ flex: 1 }}>
          <SkeletonBase $height="28px" $width="300px" style={{ marginBottom: '8px' }} />
          <SkeletonBase $height="16px" $width="400px" />
        </div>
      </div>
    </SkeletonCard>

    {/* Filter section skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="20px" $width="150px" style={{ marginBottom: '16px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index}>
            <SkeletonBase $height="14px" $width="80px" style={{ marginBottom: '8px' }} />
            <SkeletonBase $height="36px" $width="100%" $borderRadius="6px" />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <SkeletonButton width="120px" height="36px" />
        <SkeletonButton width="100px" height="36px" />
      </div>
    </SkeletonCard>

    {/* Stats grid skeleton */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonStatsCard key={index} />
      ))}
    </div>

    {/* Charts skeleton */}
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
      <SkeletonChart height="300px" />
      <SkeletonChart height="300px" />
    </div>

    {/* Recent activity skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="20px" $width="150px" style={{ marginBottom: '20px' }} />
      <SkeletonList items={5} />
    </SkeletonCard>
  </div>
);

// Assessments list skeleton
export const SkeletonAssessmentsList: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
    {/* Header skeleton */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <SkeletonTitle width="200px" />
      <div style={{ display: 'flex', gap: '12px' }}>
        <SkeletonButton width="100px" />
        <SkeletonButton width="80px" />
      </div>
    </div>

    {/* Filters skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="20px" $width="120px" style={{ marginBottom: '16px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index}>
            <SkeletonBase $height="14px" $width="80px" style={{ marginBottom: '8px' }} />
            <SkeletonBase $height="36px" $width="100%" $borderRadius="6px" />
          </div>
        ))}
      </div>
    </SkeletonCard>

    {/* Grid view skeleton */}
    <SkeletonGrid items={12} columns={3} cardHeight="250px" />
  </div>
);

// Facility management skeleton
export const SkeletonFacilityManagement: React.FC = () => (
  <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
    {/* Header skeleton */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
      <SkeletonTitle width="250px" />
      <div style={{ display: 'flex', gap: '12px' }}>
        <SkeletonButton width="140px" />
        <SkeletonButton width="120px" />
      </div>
    </div>

    {/* Facilities grid skeleton */}
    <SkeletonGrid items={6} columns={3} cardHeight="200px" />
  </div>
);

// Reports skeleton
export const SkeletonReports: React.FC = () => (
  <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
    {/* Header skeleton */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
      <SkeletonTitle width="150px" />
      <SkeletonButton width="120px" />
    </div>

    {/* Filters skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="18px" $width="120px" style={{ marginBottom: '20px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <SkeletonBase $height="14px" $width="100px" style={{ marginBottom: '8px' }} />
            <SkeletonBase $height="44px" $width="100%" $borderRadius="8px" />
          </div>
        ))}
      </div>
    </SkeletonCard>

    {/* Report content skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="20px" $width="200px" style={{ marginBottom: '20px' }} />
      <SkeletonTable rows={8} columns={5} />
    </SkeletonCard>
  </div>
);

// Audit logs skeleton
export const SkeletonAuditLogs: React.FC = () => (
  <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
    {/* Header skeleton */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
      <SkeletonTitle width="150px" />
      <SkeletonButton width="100px" />
    </div>

    {/* Filters skeleton */}
    <SkeletonCard>
      <SkeletonBase $height="18px" $width="120px" style={{ marginBottom: '20px' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <SkeletonBase $height="14px" $width="80px" style={{ marginBottom: '8px' }} />
            <SkeletonBase $height="36px" $width="100%" $borderRadius="6px" />
          </div>
        ))}
      </div>
    </SkeletonCard>

    {/* Table skeleton */}
    <SkeletonCard>
      <SkeletonTable rows={10} columns={6} />
    </SkeletonCard>
  </div>
);

export default SkeletonBase;
